import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ThemeContext = createContext();

const THEME_STORAGE_KEY = 'cfyLandingTheme';

// Sitewide light/dark theme toggle state. Originally local useState inside
// LandingPage.js (the toggle button used to live floating above that
// page's own Topbar instance, so local state was enough). The founder
// wants the toggle button relocated into the real, reusable Topbar
// (TopbarDesktop.js / TopbarMobileMenu.js) so it shows up on every page,
// which means both the Topbar and LandingPage now need to read/write the
// *same* theme value -- hence lifting this into a small context provider
// mounted once, high in the tree (see app.js's `Configurations`, shared by
// both ClientApp and ServerApp) rather than duplicating the
// useState+localStorage wiring in multiple places.
//
// Note: today only LandingPage.module.css (plus EventCard/SubscriptionCard,
// which key off the same [data-cfy-theme='dark'] selector scoped to
// LandingPage's own root element) actually react to this value -- moving
// the button doesn't retheme the rest of the site, it just makes the
// control reachable from anywhere, exactly like the founder asked for.
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    try {
      return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    } catch (e) {
      return 'light';
    }
  });

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch (e) {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Mirror the theme onto <html> so any page's stylesheet can key off
  // [data-cfy-theme='dark'], not just LandingPage's own scoped root -- that
  // element is the only carrier today, which is why the toggle is visible
  // sitewide but does nothing off the landing page.
  //
  // This deliberately sets the attribute ONLY. The global --cfy-* values in
  // discoveryTheme.css stay exactly as they are: they are already the dark
  // palette, and ProfilePage.module.css reads --cfy-ink* 207 times expecting
  // that, so redefining them here would repaint the coach profile.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-cfy-theme', theme);
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
};

// Falls back to light/no-op (rather than throwing) if ever read outside the
// provider, so an uncovered render path (tests, a future entry point)
// degrades quietly instead of crashing the page.
export const useTheme = () => {
  const context = useContext(ThemeContext);
  return context || { theme: 'light', toggleTheme: () => {} };
};
